import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  storeAuthData,
  getStoredToken,
  getStoredSsoToken,
  clearAuthData,
  getStoredUser,
  extractTokenFromUrl,
  isValidTokenFormat,
  decodeToken,
} from '@/utils/auth';
import { parseIframeParams, type IframeParams } from '@/lib/iframe-params';

interface AuthContextType {
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: Error | null;
  loginWithOAuthCode?: (token: string, user: any) => Promise<void>;
  logout: () => void;
  user: any | null;
  iframeParams: IframeParams;
  ssoUserId: string | null;
  username: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [iframeParams, setIframeParams] = useState<IframeParams>({});
  const [ssoUserId, setSsoUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    clearAuthData();
  };

  // OAuth login function that accepts token and user data from OAuth callback
  const loginWithOAuthCode = async (token: string, user: any) => {
    try {
      setIsLoading(true);

      // Store auth data
      storeAuthData({
        token,
        user,
      });

      setAuthToken(token);
      setUser(user);
      setSsoUserId(user.sso_user_id || user.id);
      setUsername(user.username);
      setIsAuthenticated(true);
      setAuthError(null);
      setIsLoading(false);
    } catch (error) {
      const authError = error instanceof Error ? error : new Error('OAuth login failed');
      setAuthError(authError);
      setIsLoading(false);
      throw error;
    }
  };

  // Simplified auth initialization for OAuth flow
  useEffect(() => {
    const initializeAuth = async () => {
      // Parse iframe params for backward compatibility
      const params = parseIframeParams();
      setIframeParams(params);

      // Check for token in URL query string (DraftKings direct SSO flow)
      const urlToken = extractTokenFromUrl();
      if (urlToken && isValidTokenFormat(urlToken)) {
        const decoded = decodeToken(urlToken);
        if (decoded) {
          const ssoId = decoded.external_id || decoded.sso_user_id || decoded.user_id || '';
          const userData = {
            id: ssoId,
            sso_user_id: ssoId,
            username: decoded.username || '',
          };
          storeAuthData({ token: urlToken, user: userData });
          setAuthToken(urlToken);
          setUser(userData);
          setSsoUserId(ssoId);
          setUsername(userData.username);
          setIsAuthenticated(true);
          setAuthError(null);
          setIsLoading(false);
          return;
        }
      }

      // Check for stored authentication (OAuth SSO token or legacy token)
      const ssoToken = getStoredSsoToken();
      const legacyToken = getStoredToken();
      const storedToken = ssoToken || legacyToken;
      const storedUser = getStoredUser();

      if (storedToken && storedUser) {
        setAuthToken(storedToken);
        setUser(storedUser);
        setSsoUserId(storedUser.sso_user_id || storedUser.id);
        setUsername(storedUser.username);
        setIsAuthenticated(true);
        setAuthError(null);
        setIsLoading(false);
        return;
      }

      // No authentication found - user needs to login via OAuth
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authToken,
        isAuthenticated,
        isLoading,
        authError,
        loginWithOAuthCode,
        logout,
        user,
        iframeParams,
        ssoUserId,
        username,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
